import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, ClipboardList, Map as MapIcon, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { PageHeader } from "@/components/common/PageHeader";
import { OrderCard } from "@/components/orders/OrderCard";
import { EmptyState } from "@/components/common/EmptyState";
import { ErrorState } from "@/components/common/ErrorState";
import { LoadingList } from "@/components/common/LoadingList";
import { OverviewMap } from "@/components/maps/OverviewMap";
import { hasCoords } from "@/components/maps/RouteMap";
import { useAuth } from "@/context/AuthContext";
import { api, errorMessage } from "@/lib/api";
import { STATUS_LABELS } from "@/lib/i18n";
import { cn } from "@/lib/utils";

const GROUPS = [
  { status: "i_arbeid", defaultOpen: true },
  { status: "ny", defaultOpen: true },
  { status: "utfort", defaultOpen: false },
  { status: "avlyst", defaultOpen: false },
];

const greeting = () => {
  const h = new Date().getHours();
  if (h < 10) return "God morgon";
  if (h < 18) return "God dag";
  return "God kveld";
};

const Group = ({ status, orders, defaultOpen }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Collapsible open={open} onOpenChange={setOpen} data-testid={`mine-ordrar-group-${status}`}>
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="focus-ring flex min-h-[44px] w-full items-center justify-between rounded-md px-1 text-left hover:bg-muted"
          data-testid={`mine-ordrar-group-${status}-toggle`}
        >
          <span className="flex items-center gap-2 text-[13px] font-semibold">
            {STATUS_LABELS[status]}
            <span className="tabular rounded bg-secondary px-1.5 py-px text-[11px] font-medium text-muted-foreground">{orders.length}</span>
          </span>
          <ChevronDown className={cn("h-4 w-4 text-muted-foreground transition-transform duration-150", open && "rotate-180")} />
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="mt-1.5 grid gap-2 md:grid-cols-2 xl:grid-cols-3">
          {orders.map((o) => (
            <OrderCard key={o.id} order={o} testId="mine-ordrar-order-card" />
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

export default function MineOrdrarPage() {
  const { user } = useAuth();
  const query = useQuery({ queryKey: ["orders", "mine"], queryFn: async () => (await api.get("/orders", { params: { mine: true } })).data });
  const orders = query.data || [];
  const activeOrders = orders.filter((o) => o.status === "i_arbeid" || o.status === "ny");
  const active = activeOrders.length;
  const mappable = activeOrders.filter((o) => hasCoords(o.logistics?.delivery));
  const [showMap, setShowMap] = useState(() => window.localStorage.getItem("mine.showMap") !== "0");
  const toggleMap = () => setShowMap((v) => { window.localStorage.setItem("mine.showMap", v ? "0" : "1"); return !v; });

  return (
    <div className="space-y-5" data-testid="mine-ordrar-page">
      <PageHeader
        eyebrow={`${greeting()}, ${user?.name?.split(" ")[0] || ""}`}
        title="Mine ordrar"
        description={query.isSuccess ? (active ? `${active} aktive oppdrag. Opne eit for å føre timar, leggje til varer eller melde utført.` : "Ingen aktive oppdrag akkurat no.") : null}
        actions={
          <>
            {mappable.length ? (
              <Button variant="outline" className={cn("h-10 bg-card", showMap && "border-foreground")} onClick={toggleMap} aria-pressed={showMap} data-testid="mine-ordrar-map-toggle">
                <MapIcon className="mr-2 h-4 w-4" /> Kart
              </Button>
            ) : null}
            <Button variant="outline" size="icon" className="h-10 w-10 bg-card md:w-auto md:px-3" onClick={() => query.refetch()} disabled={query.isFetching} aria-label="Oppdater" data-testid="mine-ordrar-refresh-button">
              <RefreshCw className={cn("h-4 w-4", query.isFetching && "animate-spin")} /> <span className="hidden md:inline">Oppdater</span>
            </Button>
          </>
        }
      />
      {showMap && mappable.length ? <OverviewMap orders={mappable} heightClassName="h-52 md:h-72" testId="mine-ordrar-map" /> : null}
      {query.isLoading ? <LoadingList /> : null}
      {query.isError ? <ErrorState message={errorMessage(query.error)} onRetry={() => query.refetch()} /> : null}
      {query.isSuccess && orders.length === 0 ? (
        <EmptyState icon={ClipboardList} title="Ingen tildelte ordrar" description="Når administrasjonen tildeler deg eit oppdrag, dukkar det opp her." data-testid="mine-ordrar-empty" />
      ) : null}
      {query.isSuccess && orders.length > 0 ? (
        <div className="space-y-5">
          {GROUPS.map((g) => {
            const list = orders.filter((o) => o.status === g.status);
            if (!list.length) return null;
            return <Group key={g.status} status={g.status} orders={list} defaultOpen={g.defaultOpen} />;
          })}
        </div>
      ) : null}
    </div>
  );
}
